import { useEffect, useState, useCallback } from "react";
import BonusGrid from "../components/BonusGrid";
import Header from "../components/Header";
import Navigation from "../components/Navigation";
import LanguageSelector from "../components/LanguageSelector";
import MaintenanceMode from "../components/MaintenanceMode";
import { useStatic } from "../context/StaticContext";
import { useBonus } from "../context/BonusContext";
import { useAdmin } from "../context/AdminContextNew";
import { useMaintenanceMode } from "../context/MaintenanceContext";
import AdminLoginModal from "../components/AdminLoginModal";
import CssEditButton from "../components/CssEditButton";
import CssPanelNew from "../components/CssPanelNew";
import { CircularProgress } from "@mui/material";
import { useCss } from "../context/CssContext";

const HomePage = () => {
  const { updateMode, isLanguage } = useStatic();
  const { fetchBonusForUser, fetchGamesForUser, userName, loading, error, userId } = useBonus();
  const { isMaintenanceMode } = useMaintenanceMode();
  const {
    showLoginModal,
    setShowLoginModal,
    handleLogin,
    showCssEditButton,
    handleEditCss,
    showCssPanel,
    handleCloseCssPanel,
  } = useAdmin();

  const { customBgImage, customBgEnabled } = useCss();

  const [spinnerSize, setSpinnerSize] = useState(60);
  const [loadingLogo, setLoadingLogo] = useState(null);
  const [footerLogo, setFooterLogo] = useState(null);
  const [loadingBgColor, setLoadingBgColor] = useState("linear-gradient(135deg, #0a1a28 0%, #0f212e 50%, #152b3c 100%)");
  const [spinnerColor, setSpinnerColor] = useState("#1475E1");
  const [loadingTextColor, setLoadingTextColor] = useState("#666");
  const [backgroundStyle, setBackgroundStyle] = useState(null);

  // Özel arkaplan değişince anlık güncelle
  useEffect(() => {
    if (customBgEnabled && customBgImage) {
      setBackgroundStyle({
        backgroundImage: `url('${customBgImage}')`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      });
    } else if (!customBgEnabled) {
      // Özel arkaplan kapatıldıysa statik dosyayı tekrar dene
      const isMobile = window.innerWidth <= 768;
      const bgImagePath = isMobile ? '/images/bg-image-mobil.webp' : '/images/bg-image.webp';
      const cached = sessionStorage.getItem(isMobile ? 'bg-image-mobile-exists' : 'bg-image-desktop-exists');
      if (cached === 'true') {
        setBackgroundStyle({
          backgroundImage: `url('${bgImagePath}')`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        });
      } else {
        setBackgroundStyle(null);
      }
    }
  }, [customBgEnabled, customBgImage]);

  // Arka plan resmini kontrol et ve varsa yükle (sadece 1 kere)
  useEffect(() => {
    const checkAndSetBackground = async () => {
      // Özel arkaplan aktifse statik dosya kontrolüne gerek yok
      if (customBgEnabled && customBgImage) return;

      const isMobile = window.innerWidth <= 768;
      const bgImagePath = isMobile ? '/images/bg-image-mobil.webp' : '/images/bg-image.webp';
      const CACHE_KEY = isMobile ? 'bg-image-mobile-exists' : 'bg-image-desktop-exists';

      // Cache kontrolü
      const cached = sessionStorage.getItem(CACHE_KEY);

      if (cached === 'false') {
        return;
      }

      if (cached === 'true') {
        setBackgroundStyle({
          backgroundImage: `url('${bgImagePath}')`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat"
        });
        return;
      }

      // Cache yok, dosyayı kontrol et
      const img = new Image();
      img.onload = () => {
        sessionStorage.setItem(CACHE_KEY, 'true');
        setBackgroundStyle({
          backgroundImage: `url('${bgImagePath}')`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat"
        });
      };
      img.onerror = () => {
        sessionStorage.setItem(CACHE_KEY, 'false');
      };
      img.src = bgImagePath;
    };

    checkAndSetBackground();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Loading spinner size ve renkleri CSS değişkeninden oku
  useEffect(() => {
    const getCSSValue = (property, fallbackKey) => {
      const value = getComputedStyle(document.documentElement)
        .getPropertyValue(property)
        .trim();
      return value || (fallbackKey ? localStorage.getItem(fallbackKey) : null) || null;
    };

    const updateLoadingSettings = () => {
      const size = getCSSValue('--loading-page-spinner-size');
      if (size) setSpinnerSize(parseInt(size) || 60);

      const bgColor = getCSSValue('--loading-page-bg-color', 'loadingPageBgColor');
      if (bgColor) setLoadingBgColor(bgColor);

      const spinnerColor = getCSSValue('--loading-page-spinner-color', 'loadingPageSpinnerColor');
      if (spinnerColor) setSpinnerColor(spinnerColor);

      const textColor = getCSSValue('--loading-page-text-color', 'loadingPageTextColor');
      if (textColor) setLoadingTextColor(textColor);
    };
    
    updateLoadingSettings();
    
    const observer = new MutationObserver(updateLoadingSettings);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['style'],
    });
    
    return () => observer.disconnect();
  }, []);

  // Logo yükleme helper
  const loadLogo = useCallback((src) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(src);
      img.onerror = reject;
      img.src = src;
    });
  }, []);

  // Logo yükleme (fallback destekli)
  const loadLogoWithFallback = useCallback(async (paths, setState) => {
    for (const path of paths) {
      try {
        const logoPath = await loadLogo(path);
        setState(logoPath);
        return;
      } catch {
        continue;
      }
    }
    setState(null);
  }, [loadLogo]);

  // Logo'ları yükle: loading (logo.webp) ve footer (long.webp)
  useEffect(() => {
    loadLogoWithFallback(['/static/image/logo.webp'], setLoadingLogo);
    loadLogoWithFallback(['/static/image/long.webp'], setFooterLogo);
  }, [loadLogoWithFallback]);

  useEffect(() => {
    fetchBonusForUser();
    fetchGamesForUser();
  }, [fetchBonusForUser, fetchGamesForUser]);

  // Bakım modu değiştiğinde bonusları ve oyunları yeniden yükle
  useEffect(() => {
    if (isMaintenanceMode) {
      fetchBonusForUser();
      fetchGamesForUser();
    }
  }, [isMaintenanceMode, fetchBonusForUser, fetchGamesForUser]);

  // Dil değiştiğinde bonusları ve oyunları yeniden yükle
  useEffect(() => {
    if (isLanguage) {
      fetchBonusForUser();
      fetchGamesForUser();
    }
  }, [isLanguage, fetchBonusForUser, fetchGamesForUser]);

  if (updateMode) {
    return <MaintenanceMode />;
  }

  // Hata durumlarını kontrol et
  if (error) {
    const getErrorMessage = (errorType) => {
      switch (errorType) {
        case "invalid_access":
          return "Geçersiz erişim";
        case "user_blocked":
          return "Kullanıcı engellenmiştir";
        case "user_not_found":
          return "Kullanıcı bulunamadı";
        case "api_error":
          return "API hatası oluştu";
        case "connection_error":
          return "Bağlantı hatası";
        default:
          return "Bilinmeyen hata";
      }
    };

    return (
      <div className="loading-container">
        <div className="error-message">
          <h2 style={{ color: "#ff4757", marginBottom: "10px" }}>
            {getErrorMessage(error)}
          </h2>
          <p style={{ color: "#666", fontSize: "14px" }}>
            Lütfen URL'yi kontrol edin veya daha sonra tekrar deneyin.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div 
        className="loading-container"
        style={{
          background: loadingBgColor,
        }}
      >
        {loadingLogo && (
          <img 
            src={loadingLogo}
            alt="Logo"
            className="loading-logo"
          />
        )}
        <CircularProgress
          size={spinnerSize}
          thickness={4}
          sx={{
            color: spinnerColor,
            "& .MuiCircularProgress-circle": {
              strokeLinecap: "round",
            },
          }}
        />
        {!userId && (
          <p style={{ marginTop: "20px", color: loadingTextColor }}>
            Geçersiz erişim
          </p>
        )}
      </div>
    );
  }
  return (
    <div className="app-container" style={backgroundStyle || {}}>
      {/* Admin Login Modal */}
      <AdminLoginModal
        open={showLoginModal}
        onClose={() => setShowLoginModal(false)}
      />

      {/* CSS Edit Button */}
      {showCssEditButton && <CssEditButton onClick={handleEditCss} />}

      {/* CSS Panel */}
      <CssPanelNew open={showCssPanel} onClose={handleCloseCssPanel} />

              {/* Language Selector - sağ üst köşede sabit */}
        {isLanguage && <LanguageSelector />}

        {/* Ana İçerik */}
        <div className="content-container">
        {/* Header */}
        <Header username={userName} />
        {/* Navigation */}
        <Navigation />

        {/* Otobonüs Bonus Kartları */}
        <BonusGrid />
       
      </div>

      {/* Footer */}
      <footer className="app-footer">
        {footerLogo && (
          <div className="footer-logo-wrapper">
            <a 
              href="https://longdijital.com/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="footer-logo-link"
            >
              <div className="footer-logo-container">
                <img 
                  src={footerLogo}
                  alt="Long Dijital Logo"
                  className="footer-logo"
                />
                <div className="footer-logo-glow"></div>
              </div>
            </a>
          </div>
        )}
      </footer>
    </div>
  );
};

export default HomePage;

